import { useRef, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';

import { GridRowModes, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, FormErrorUI, DatagridPremiumUi, InputUI, CheckboxUI } from '../Interface';
import useWindowSubtractedHeight from '../../hooks/useWindowSubtractedHeight';

const ServiceTaskCheckList = ({ disabled, defaultServiceTaskCheckList }) => {
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const gridHeight = useWindowSubtractedHeight({ otherContentHeight: 600, minHeight: 480 });

  const getValueIndex = (values, id) => {
    return values.serviceTaskCheckList.findIndex((rc) => rc.id === id);
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'name',
          headerName: 'Megnevezés',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.serviceTaskCheckList?.[valueIndex]?.name;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`serviceTaskCheckList.${valueIndex}.name`}
                type="text"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'name'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.serviceTaskCheckList?.[valueIndex]?.name} />}
                onChange={(e) => {
                  setFieldValue(`serviceTaskCheckList.${valueIndex}.name`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`serviceTaskCheckList.${valueIndex}.name`)}
              />
            );
          },
        },

        {
          field: 'note',
          headerName: 'Megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.serviceTaskCheckList?.[valueIndex]?.note;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`serviceTaskCheckList.${valueIndex}.note`}
                type="text"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'note'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.serviceTaskCheckList?.[valueIndex]?.note} />}
                onChange={(e) => {
                  setFieldValue(`serviceTaskCheckList.${valueIndex}.note`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`serviceTaskCheckList.${valueIndex}.note`)}
              />
            );
          },
        },

        {
          field: 'attachRequired',
          headerName: 'Melléklet szükséges',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`serviceTaskCheckList.${valueIndex}.attachRequired`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.serviceTaskCheckList?.[valueIndex]?.attachRequired} />}
                checked={values.serviceTaskCheckList?.[valueIndex]?.attachRequired ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`serviceTaskCheckList.${valueIndex}.attachRequired`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },

        {
          field: 'descriptionRequired',
          headerName: 'Leírás szükséges',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`serviceTaskCheckList.${valueIndex}.descriptionRequired`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.serviceTaskCheckList?.[valueIndex]?.descriptionRequired} />}
                checked={values.serviceTaskCheckList?.[valueIndex]?.descriptionRequired ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`serviceTaskCheckList.${valueIndex}.descriptionRequired`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },

        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={disabled}
              />,
            ];
          },
        },
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.serviceTaskCheckList, errors, setFieldValue, validateField, disabled]
  );

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="serviceTaskCheckList">
              {({ remove }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.ServiceTaskChecklist}
                  height={gridHeight}
                  columns={columns(remove)}
                  rows={values?.serviceTaskCheckList ?? []}
                  editMode="row"
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;
                    focusedFieldRef.current = params.field;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceTaskCheckList;
